import { DeprecatedLink, TextField } from "@resource/atlas";
import { atlasSearch } from "@resource/atlas/icons";
import { strings } from "@resource/common";
import { GetAtsStagesForMappingVariables } from "generated/schemaTypes";
import _, { isNil } from "lodash";
import { useMemo, useState } from "react";
import {
  DragDropContext,
  DragStart,
  DragUpdate,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { formatEntity } from "shared/constants/entities";

import ATSStage from "./ATSStage";
import GuideTemplateStage from "./GuideTemplateStage";

export interface Stage {
  id: string;
  name: string;
  avgPosition: number | null;
  jobRoleGuideTemplateIds: string[];
}
export interface GuideTemplateStage {
  id: string;
  name: string;
  position: number;
}

export interface Plan {
  id: string;
  name: string;
  guideTemplateStages: GuideTemplateStage[];
}

interface Props {
  name?: string;
  plan: Plan;
  stages: Stage[];
  forMultipleJobs: boolean;
  mapping: Record<string, Stage[]>;
  updateMapping: (mapping: Record<string, Stage[]>) => void;
  filters: GetAtsStagesForMappingVariables["where"];
  updateFilters: (filters: GetAtsStagesForMappingVariables["where"]) => void;
}

function GuideTemplateMapper({
  name,
  plan,
  stages,
  mapping,
  updateMapping,
  filters,
  updateFilters,
  forMultipleJobs,
}: Props) {
  const debouncedUpdateFilters = useMemo(
    () => _.debounce(updateFilters, 200),
    [updateFilters]
  );
  const [draggingStage, setDraggingStage] = useState<Stage | null>(null);
  const [overDroppable, setOverDroppable] = useState<string | null>(null);

  const unmappedStages = useMemo(() => {
    const mappedStages = Object.values(mapping).reduce(
      (all, array) => all.concat(array),
      []
    );
    return stages.filter(
      (stage) =>
        !mappedStages.some((mappedStage) => mappedStage.id === stage.id)
    );
  }, [stages, mapping]);

  const onDragStart = (result: DragStart) => {
    const stage = stages.find((item) => item.id === result.draggableId);
    setDraggingStage(stage ?? null);
  };

  const onDragUpdate = (result: DragUpdate) => {
    const { destination } = result;
    if (destination) {
      setOverDroppable(destination.droppableId);
    }
  };

  const onDragEnd = (result: DropResult) => {
    setDraggingStage(null);
    setOverDroppable(null);
    if (result.destination) {
      const stageId = result.draggableId;
      const { droppableId } = result.destination;
      const sourceId = result.source.droppableId;
      if (sourceId !== droppableId) {
        const stage = stages.find((item) => item.id === stageId);
        if (stage) {
          const newMapping = { ...mapping };
          if (droppableId !== "stages") {
            const guideTemplateStages = newMapping[droppableId] ?? [];
            newMapping[droppableId] = forMultipleJobs
              ? [...guideTemplateStages, stage]
              : [stage];
          }
          newMapping[sourceId] = _.reject(newMapping[sourceId], {
            id: stage.id,
          });
          if (newMapping[sourceId].length === 0) {
            delete newMapping[sourceId];
          }
          updateMapping(newMapping);
        }
      }
    }
  };

  return (
    <DragDropContext
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      onDragUpdate={onDragUpdate}
    >
      {/* Custom Height Style added to prevent double scroll on smaller screens */}
      <div className="flex h-[calc(100vh-17.5rem)]">
        <div className="flex flex-col w-1/2 p-6 overflow-y-auto border-r border-light-gray-600">
          <p className="text-body-md">
            Keep your candidates&apos; guides in sync. Drag Greenhouse stages
            from the left to the corresponding stage of the{" "}
            {formatEntity("job template")} {formatEntity("interview plan")}.
            <span>&nbsp;</span>
            {/* TODO: Replace Learn More href with proper link. */}
            <DeprecatedLink href="/">Learn more</DeprecatedLink>
          </p>
          <TextField
            isClearable
            className="mt-4"
            icon={atlasSearch}
            placeholder="Search by stage name"
            defaultValue={filters.search ?? ""}
            onClear={() => {
              debouncedUpdateFilters.cancel();
              debouncedUpdateFilters({ ...filters, search: "" });
            }}
            onChange={(search) => {
              debouncedUpdateFilters.cancel();
              debouncedUpdateFilters({ ...filters, search });
            }}
          />
          {name ? (
            <>
              <h2 className="text-h4 mt-5">{name}</h2>
              <p className="text-body-md text-subtle">
                {strings.pluralize("unmapped stage", unmappedStages.length)}
              </p>
            </>
          ) : (
            <p className="text-h4 mt-5">
              {strings.pluralize("unmapped stage", unmappedStages.length)}
            </p>
          )}
          <Droppable droppableId="stages">
            {(provided) => (
              <div className="mt-4 flex-grow" ref={provided.innerRef}>
                {_(unmappedStages)
                  .sortBy(({ avgPosition, name: stageName }) => {
                    if (isNil(avgPosition)) {
                      return stageName;
                    }
                    return avgPosition;
                  })
                  .map((stage, index) => (
                    <div key={stage.id} className="mt-3">
                      <ATSStage
                        stage={stage}
                        index={index}
                        showJobsCount={forMultipleJobs}
                      />
                    </div>
                  ))
                  .value()}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
        <div className="w-1/2 bg-light-gray-400 p-6 overflow-y-auto overflow-x-hidden">
          <div>
            <h2 className="text-h4">
              {plan.name} {formatEntity("job template")}{" "}
              {formatEntity("interview plan")}
            </h2>
            <p className="text-body-md text-subtle">
              {strings.pluralize("stage", plan.guideTemplateStages.length)}
            </p>
          </div>
          {_(plan.guideTemplateStages)
            .sortBy("position")
            .map((stage, index) => (
              <GuideTemplateStage
                index={index}
                key={stage.id}
                draggingStage={draggingStage}
                forMultipleJobs={forMultipleJobs}
                guideTemplateStage={stage}
                stages={mapping[stage.id] ?? []}
                isOverThis={overDroppable === stage.id}
              />
            ))
            .value()}
        </div>
      </div>
    </DragDropContext>
  );
}

export default GuideTemplateMapper;
